import { useState, useEffect } from 'react';

export default function SolarCalculator() {
  const [acres, setAcres] = useState(25);
  const [acresPerMW, setAcresPerMW] = useState(5);
  const [serviceType, setServiceType] = useState<'visual' | 'thermal' | 'full'>('thermal');
  const [priorityTurnaround, setPriorityTurnaround] = useState(false);
  const [outsideMobilization, setOutsideMobilization] = useState(false);

  const [estimatedMW, setEstimatedMW] = useState(0);
  const [perMWRateLow, setPerMWRateLow] = useState(0);
  const [perMWRateHigh, setPerMWRateHigh] = useState(0);
  const [totalLow, setTotalLow] = useState(0);
  const [totalHigh, setTotalHigh] = useState(0);

  useEffect(() => {
    calculatePricing();
  }, [acres, acresPerMW, serviceType, priorityTurnaround]);

  const calculatePricing = () => {
    // Calculate MW
    const mw = acres / acresPerMW;
    setEstimatedMW(mw);

    // Base rates per MW (at 100+ acres)
    let baseLow = 0;
    let baseHigh = 0;

    switch (serviceType) {
      case 'visual':
        baseLow = 150;
        baseHigh = 300;
        break;
      case 'thermal':
        baseLow = 300;
        baseHigh = 600;
        break;
      case 'full':
        baseLow = 500;
        baseHigh = 1200;
        break;
    }

    // Small-site premium multiplier
    const multiplier = acres >= 100 
      ? 1.0 
      : 1.6 - ((Math.min(acres, 100) - 1) * (0.6 / 99));

    // Adjusted rates
    const adjustedLow = baseLow * multiplier;
    const adjustedHigh = baseHigh * multiplier;

    setPerMWRateLow(adjustedLow);
    setPerMWRateHigh(adjustedHigh);

    // Total estimate
    let low = mw * adjustedLow;
    let high = mw * adjustedHigh;

    // Apply priority turnaround
    if (priorityTurnaround) {
      low *= 1.15;
      high *= 1.15;
    }

    setTotalLow(Math.round(low));
    setTotalHigh(Math.round(high));
  };

  const handleAcresChange = (value: number) => {
    const clampedValue = Math.max(1, Math.min(500, value));
    setAcres(clampedValue);
  };

  return (
    <div id="pricing-calculator" className="bg-white rounded-lg shadow-sm border border-stone-200 p-8">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-stone-900 mb-3">Solar Farm Drone Inspection Estimator</h2>
        <p className="text-stone-600">
          Choose your service, enter acreage, and see an estimated range. Pricing scales to be fair on small sites and efficient at utility scale.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Calculator Inputs */}
        <div className="space-y-6">
          {/* Site Size */}
          <div>
            <label className="block text-sm font-semibold text-stone-900 mb-3">
              Site Size (Acres)
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="1"
                max="500"
                value={acres}
                onChange={(e) => handleAcresChange(Number(e.target.value))}
                className="flex-1 h-2 bg-stone-200 rounded-lg appearance-none cursor-pointer accent-emerald-600"
              />
              <input
                type="number"
                min="1"
                max="500"
                value={acres}
                onChange={(e) => handleAcresChange(Number(e.target.value))}
                className="w-24 px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
          </div>

          {/* Acres-to-MW Conversion */}
          <div>
            <label className="block text-sm font-semibold text-stone-900 mb-3">
              Acres-to-MW Conversion
            </label>
            <select
              value={acresPerMW}
              onChange={(e) => setAcresPerMW(Number(e.target.value))}
              className="w-full px-4 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value={4}>Dense: 4 acres per MW</option>
              <option value={5}>Typical: 5 acres per MW</option>
              <option value={6}>Conservative: 6 acres per MW</option>
            </select>
          </div>

          {/* Service Selector */}
          <div>
            <label className="block text-sm font-semibold text-stone-900 mb-3">
              Service Type
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 border border-stone-300 rounded-md cursor-pointer hover:bg-stone-50 transition-colors">
                <input
                  type="radio"
                  name="service"
                  value="visual"
                  checked={serviceType === 'visual'}
                  onChange={() => setServiceType('visual')}
                  className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-sm font-medium text-stone-900">Visual Inspection (RGB)</span>
              </label>
              <label className="flex items-center gap-3 p-3 border border-stone-300 rounded-md cursor-pointer hover:bg-stone-50 transition-colors">
                <input
                  type="radio"
                  name="service"
                  value="thermal"
                  checked={serviceType === 'thermal'}
                  onChange={() => setServiceType('thermal')}
                  className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-sm font-medium text-stone-900">Thermal Inspection (Infrared)</span>
              </label>
              <label className="flex items-center gap-3 p-3 border border-stone-300 rounded-md cursor-pointer hover:bg-stone-50 transition-colors">
                <input
                  type="radio"
                  name="service"
                  value="full"
                  checked={serviceType === 'full'}
                  onChange={() => setServiceType('full')}
                  className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-sm font-medium text-stone-900">Full Diagnostic Package (Thermal + Visual + Reporting)</span>
              </label>
            </div>
          </div>

          {/* Add-ons */}
          <div>
            <label className="block text-sm font-semibold text-stone-900 mb-3">
              Add-ons
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 border border-stone-300 rounded-md cursor-pointer hover:bg-stone-50 transition-colors">
                <input
                  type="checkbox"
                  checked={priorityTurnaround}
                  onChange={(e) => setPriorityTurnaround(e.target.checked)}
                  className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                />
                <span className="text-sm font-medium text-stone-900">Priority turnaround (48 hours): +15%</span>
              </label>
              <label className="flex items-center gap-3 p-3 border border-stone-300 rounded-md cursor-pointer hover:bg-stone-50 transition-colors">
                <input
                  type="checkbox"
                  checked={outsideMobilization}
                  onChange={(e) => setOutsideMobilization(e.target.checked)}
                  className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-stone-900">Outside 60 miles mobilization</span>
                  <p className="text-xs text-stone-500 mt-1">Travel billed separately</p>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Results Panel */}
        <div className="bg-stone-50 rounded-lg p-6 border border-stone-200 h-fit">
          <h3 className="text-xl font-bold text-stone-900 mb-6">Estimated Pricing</h3>
          
          <div className="space-y-4 mb-6">
            <div className="flex justify-between items-center pb-3 border-b border-stone-300">
              <span className="text-sm font-medium text-stone-600">Estimated MW</span>
              <span className="text-lg font-bold text-stone-900">{estimatedMW.toFixed(1)} MW</span>
            </div>
            
            <div className="flex justify-between items-center pb-3 border-b border-stone-300">
              <span className="text-sm font-medium text-stone-600">Per-MW Rate</span>
              <span className="text-lg font-bold text-stone-900">
                ${Math.round(perMWRateLow)} to ${Math.round(perMWRateHigh)}{serviceType === 'full' && '+'} per MW
              </span>
            </div>
            
            <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
              <div className="text-sm font-medium text-emerald-800 mb-2">Estimated Price Range</div>
              <div className="text-3xl font-bold text-emerald-900">
                ${totalLow.toLocaleString()} to ${totalHigh.toLocaleString()}{serviceType === 'full' && '+'}
              </div>
            </div>
          </div>
          
          <p className="text-xs text-stone-600 mb-6">
            Final pricing depends on layout, access, and reporting requirements.
          </p>
          
          <div className="space-y-3">
            <a
              href="/instant-quote"
              className="block w-full bg-emerald-700 text-white text-center px-6 py-3 rounded-md font-semibold hover:bg-emerald-800 transition-colors whitespace-nowrap"
            >
              Get a Firm Quote
            </a>
            <a
              href="tel:+1234567890"
              className="block w-full bg-white text-stone-900 text-center px-6 py-3 rounded-md font-semibold border border-stone-300 hover:bg-stone-50 transition-colors whitespace-nowrap"
            >
              Call for Details
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
